import React, { useState, useMemo } from 'react';
import { useBookmarks } from '../hooks/useBookmarks';
import { useAuth } from '../contexts/AuthContext';
import BookmarkCard from '../components/BookmarkCard';
import SearchBar from '../components/SearchBar';
import UserProfile from '../components/UserProfile';
import TutorialModal from '../components/TutorialModal';
import OfflineStatusBar from '../components/OfflineStatusBar';
import { LoadingSpinner, ErrorMessage, EmptyState } from '../components/UIComponents';
import QuickAddForm from '../components/QuickAddForm';
import ConfirmationModal from '../components/ConfirmationModal'; // Import ConfirmationModal

const Home = () => {
  const { 
    bookmarks, 
    loading, 
    error, 
    isOnline,
    addBookmark, 
    deleteBookmark, 
    updateBookmark,
    refreshBookmarks,
    // Add the new function from useBookmarks
    triggerMetadataExtraction 
  } = useBookmarks();
  
  const { showTutorial, closeTutorial } = useAuth();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationModalProps, setConfirmationModalProps] = useState({});

  // Filter bookmarks based on search term
  const filteredBookmarks = useMemo(() => {
    if (!searchTerm.trim()) return bookmarks;
    
    const term = searchTerm.toLowerCase();
    return bookmarks.filter(bookmark => 
      bookmark.title?.toLowerCase().includes(term) ||
      bookmark.url?.toLowerCase().includes(term) ||
      bookmark.description?.toLowerCase().includes(term) ||
      bookmark.tags?.some(tag => tag.toLowerCase().includes(term))
    );
  }, [bookmarks, searchTerm]);

  const handleAddBookmark = async (bookmarkData) => {
    setIsSubmitting(true);
    try {
      await addBookmark(bookmarkData);
    } catch (error) {
      console.error('Failed to add bookmark:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteBookmark = async (id) => {
    try {
      await deleteBookmark(id);
    } catch (error) {
      console.error('Failed to delete bookmark:', error);
    }
  };

  const handleUpdateBookmark = async (id, updates) => {
    try {
      await updateBookmark(id, updates);
    } catch (error) {
      console.error('Failed to update bookmark:', error);
    }
  };

  const handleRefreshMetadata = async (bookmarkId, url) => {
    if (!triggerMetadataExtraction) {
      console.error('triggerMetadataExtraction function is not available on useBookmarks');
      return;
    }

    setConfirmationModalProps({
      title: 'Refresh Metadata?',
      message: 'This will fetch the latest title and description from the website and overwrite any existing ones. Are you sure?',
      confirmText: 'Refresh',
      onConfirm: async () => {
        setShowConfirmationModal(false);
        try {
          await triggerMetadataExtraction(bookmarkId, url);
        } catch (error) {
          console.error('Failed to trigger metadata extraction:', error);
          // Optionally, set an error state to inform the user, e.g., using a toast notification.
        }
      },
      onClose: () => setShowConfirmationModal(false),
    });
    setShowConfirmationModal(true);
  };

  if (loading && bookmarks.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <LoadingSpinner message="Loading your bookmarks..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-50 to-blue-50 border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="relative flex items-center">
            {/* Spacer for left side (keeps title centered) */}
            <div className="w-10 flex-shrink-0"></div>
            
            {/* Logo/Title - Centered */}
            <div className="flex-1 text-center">
              <h1 className="text-3xl font-black mb-1 tracking-tight">
                <span className="bg-gradient-to-r from-teal-600 via-teal-500 to-emerald-500 bg-clip-text text-transparent">
                  EZ
                </span>
                <span className="text-slate-800 ml-3 font-bold">Bookmarks</span>
              </h1>
              
              <p className="text-slate-600 text-sm font-medium tracking-wide">
                Save and organize your favorite links with ease
              </p>
            </div>

            {/* User Profile - Right aligned */}
            <div className="w-10 flex-shrink-0 flex justify-end">
              <UserProfile />
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Error Message */}
        {error && (
          <div className="mb-8">
            <ErrorMessage 
              message={error} 
              onRetry={refreshBookmarks}
              isOffline={!isOnline}
            />
          </div>
        )}

        {/* Search Bar */}
        <SearchBar
          onSearch={setSearchTerm}
          totalCount={filteredBookmarks.length}
        />

        {/* Quick Add Form - Always Visible */}
        <QuickAddForm
          onSubmit={handleAddBookmark}
          loading={isSubmitting}
        />

        {/* Bookmarks List */}
        {bookmarks.length === 0 && !loading ? (
          <EmptyState
            title="No bookmarks yet"
            description="Add your first bookmark above! Copy a URL from any page and paste it here."
          />
        ) : filteredBookmarks.length === 0 && searchTerm ? (
          <EmptyState
            title="No matching bookmarks"
            description={`No bookmarks found for "${searchTerm}". Try a different search term.`}
          />
        ) : (
          <div className="space-y-6">
            {filteredBookmarks.map((bookmark) => (
              <BookmarkCard
                key={bookmark.id}
                bookmark={bookmark}
                onDelete={handleDeleteBookmark}
                onUpdate={handleUpdateBookmark}
                onRefreshMetadata={handleRefreshMetadata} // Pass the handler to BookmarkCard
              />
            ))}
          </div>
        )}

        {/* Quick Stats */}
        {bookmarks.length > 0 && (
          <div className="mt-12 text-center">
            <div className="inline-flex items-center px-4 py-2 bg-white rounded-full shadow-sm border border-border-light">
              <span className="text-gray-600 text-sm font-medium">
                {bookmarks.length} total bookmark{bookmarks.length !== 1 ? 's' : ''}
                {searchTerm && (
                  <>
                    <span className="mx-2 text-gray-400">•</span>
                    <span className="text-teal-600">{filteredBookmarks.length} matching</span>
                  </>
                )}
              </span>
            </div>
          </div>
        )}
      </div>
      
      {/* Tutorial Modal */}
      <TutorialModal 
        isOpen={showTutorial} 
        onClose={closeTutorial} 
      />
      
      {/* Offline Status Bar */}
      <OfflineStatusBar />

      <ConfirmationModal
        isOpen={showConfirmationModal}
        {...confirmationModalProps}
      />
    </div>
  );
};

export default Home;
